import type { ProposalBuildPhase } from '@shared/api';
import type { DocumentSection, DocumentSectionType } from '@shared/types';

/**
 * Once the build reaches one of these phases the rail and the per-page
 * "still building" skeletons must clear even if the client's own
 * `buildRequested` flag is still (sticky) true — e.g. a narrative page that
 * never got a copy pass in a failed group must not look stuck forever
 * (ProposalView.tsx G3).
 */
const TERMINAL_BUILD_PHASES = new Set<ProposalBuildPhase>(['ready', 'needs_review', 'failed']);

/**
 * True from the rep's Build click through the last narrative page's copy
 * landing — the window in which the build rail (StageTracker) and the
 * per-page building skeletons should be visible.
 *
 * `buildRequested` bridges the gap between the click and the server's own
 * `phase` flipping to `building` (the server only does that once the first
 * brief/build tool actually starts — see toolStarted in src/state/build.ts —
 * which can lag the click by several seconds while the model's first turn is
 * in flight). `phase` is undefined for the moment before the first build
 * snapshot has loaded at all.
 */
export function isBuildInFlight(
  phase: ProposalBuildPhase | undefined,
  buildRequested: boolean,
): boolean {
  if (phase !== undefined && TERMINAL_BUILD_PHASES.has(phase)) return false;
  return buildRequested || phase === 'building';
}

/**
 * Section types build_deck ever writes narrative copy on (mirrors
 * `NARRATIVE_TYPES` in src/agent/build-deck.ts — keep in sync; web/ cannot
 * import that server-only module directly, so this list is a deliberate
 * duplicate, not a re-export). Everything else binds live to proposal state
 * or a content-library default and never carries a "waiting for the agent"
 * look.
 */
export const NARRATIVE_BUILD_TYPES: ReadonlySet<DocumentSectionType> = new Set<DocumentSectionType>([
  'cover',
  'campaign_goal',
  'overview',
  'strategy',
  'media_mix',
  'product_divider',
  'product_plan',
  'tactic_group',
  'thank_you',
]);

/**
 * Ids of sections that should render `PageCard`'s `building` skeleton: the
 * build is in flight AND the section's copy is still the unwritten template
 * default (`content.copyOrigin === 'template'`). Returns `[]` once the build
 * is no longer in flight, regardless of any section's copyOrigin, so a page
 * left `template` by a failed copy group never looks stuck after the build
 * ends.
 */
export function deriveBuildingSectionIds(
  sections: DocumentSection[],
  buildInFlight: boolean,
): string[] {
  if (!buildInFlight) return [];
  const ids: string[] = [];
  for (const section of sections) {
    if (NARRATIVE_BUILD_TYPES.has(section.type) && section.content.copyOrigin === 'template') {
      ids.push(section.id);
    }
  }
  return ids;
}
